import { observer } from 'mobx-react-lite'
import {
  Button,
  Checkbox,
  Divider,
  Icon,
  Radio,
  Scrollbar,
  Switch,
  Tooltip,
  Typography,
} from 'shared/ui'
import { SelectField } from 'shared/ui/Select/SelectField/SelectField'
import { useMemberContext } from 'pages/settings/pages/members/context/memberContext'

import styles from './styles.module.scss'

export const MemberPermissions = observer(() => {
  const {
    member,
    onSaveMemberPermissions,
    memberPermissionsStore: {
      permissionsArray,
      loading,
      setPermission,
      contactAccessValue,
      hasChanges,
      cancelPermissions,
      roleItems,
      setRole,
      role,
      useOrganizationSettings,
      setUseOrganizationSettings,
      isRoleChangeDisabled,
    },
  } = useMemberContext()

  if (!member) return null

  return (
    <div className={styles.wrapper}>
      <Scrollbar>
        <div className={styles.container}>
          <div>
            <div className={styles.rolesWrap}>
              <div className={styles.labelWrap}>
                <Typography
                  ariaLabel='roleLabel'
                  variant={'body-lg-medium'}
                  color={'var(--content-primary-primary)'}
                >
                  Role
                </Typography>
                <Typography
                  ariaLabel='roleDescription'
                  variant={'body-md-regular'}
                  color={'var(--content-primary-tertiary)'}
                >
                  Define the workspace role of the member
                </Typography>
              </div>
              <SelectField
                boxWidth={'100px'}
                value={role}
                size='medium'
                setValue={setRole}
                disabled={loading || isRoleChangeDisabled}
                withSearch={false}
                dropdownProps={{
                  width: '140px',
                }}
                items={roleItems}
              />
            </div>
            <Divider
              style={{
                borderColor: 'var(--border-neutral-default)',
                marginBottom: '10px',
              }}
            />
          </div>
          <div>
            <Typography
              ariaLabel='permissionsLabel'
              variant={'body-lg-medium'}
              color={'var(--content-primary-primary)'}
            >
              Permissions
            </Typography>
            <div className={styles.organizationSettingsWrap}>
              <Typography
                ariaLabel='organizationSettingsDescription'
                variant={'body-md-regular'}
                color={'var(--content-primary-primary)'}
              >
                Use organization settings
              </Typography>
              <Switch
                value={useOrganizationSettings}
                onChange={setUseOrganizationSettings}
                size='middle'
                disabled={loading}
              />
            </div>
          </div>
          {permissionsArray.map((item) => (
            <div key={item.key} className={styles.permissionsWrap}>
              <Typography
                ariaLabel={item.label}
                variant={'body-md-semibold'}
                color={'var(--content-primary-primary)'}
              >
                {item.label}
              </Typography>
              <div className={styles.permissions}>
                {item.permissions.map((permission, idx) => (
                  <div key={`${permission.key}-${idx}`} className={styles.permission}>
                    <Divider
                      style={{
                        borderColor: 'var(--border-neutral-default)',
                      }}
                    />
                    <div className={styles.permissionContent}>
                      <Typography
                        ariaLabel={permission.label}
                        variant={'body-md-regular'}
                        color={
                          permission.disabled || useOrganizationSettings
                            ? 'var(--content-primary-disabled)'
                            : 'var(--content-primary-primary)'
                        }
                      >
                        {permission.label}

                        {permission.tooltip && (
                          <Tooltip
                            placement={'top'}
                            label={permission.tooltip.label}
                            className={styles.permissionTooltip}
                            {...(permission.tooltip?.tooltipProps
                              ? permission.tooltip.tooltipProps
                              : {})}
                          >
                            <Icon
                              icon={'question'}
                              color={'var(--content-neutral-primary)'}
                              fontSize={14}
                            />
                          </Tooltip>
                        )}
                      </Typography>
                      <div className={styles.permissionValue}>
                        {permission.custom && (
                          <Tooltip label='Custom permission' placement='top'>
                            <Icon
                              icon={'infoCircle'}
                              color={'var(--content-neutral-primary)'}
                              fontSize={14}
                            />
                          </Tooltip>
                        )}
                        {typeof permission.value === 'boolean' ? (
                          <Checkbox
                            id={permission.key}
                            checked={permission.value}
                            disabled={!!permission.disabled || loading || useOrganizationSettings}
                            onChange={() => setPermission(permission.key, !permission.value)}
                            size={20}
                            style={{ cursor: 'pointer' }}
                          />
                        ) : (
                          <Radio
                            checked={permission.value === contactAccessValue}
                            disabled={!!permission.disabled || loading || useOrganizationSettings}
                            size={'medium'}
                            onChange={() => setPermission(permission.key, permission.value)}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Scrollbar>
      {hasChanges && (
        <div className={styles.actions}>
          <Button
            text={'Save'}
            disabled={loading}
            className={styles.button}
            onClick={onSaveMemberPermissions}
            typeBtn={'contained'}
            contained={'primary'}
            size={'medium'}
          />
          <Button
            text={'Cancel'}
            disabled={loading}
            className={styles.button}
            onClick={cancelPermissions}
            typeBtn={'contained'}
            contained={'secondary'}
            size={'medium'}
          />
        </div>
      )}
    </div>
  )
})
